'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { LEAD_NAME_MAX_LENGTH } from '@nwwp/database'
import { useTrackFormStepViewed } from '@nwwp/segment-analytics'
import { cn, Input } from '@nwwp/ui-shadcn'
import { useVeritonic } from '@nwwp/veritonic'
import { createScheduleFormLead } from '~/actions/create-schedule-form-lead'
import { sendOtpMessage } from '~/actions/otp'
import { NumberInput } from '~/components/NumberInput'
import { RangeSelectInput } from '~/components/RangeSelectInput'
import { buildScheduleRedirectUrl, PAID_MEDIA_CALENDLY_URL } from '~/utils/ctas'
import { clearSchedulePrefill, loadScheduleFormPrefill, saveSchedulePrefill } from '~/utils/schedule-prefill'
import { getTrackingParams } from '~/utils/tracking'
import { DisqualifiedView } from './DisqualifiedView'
import { OtpDialog } from './OtpDialog'

const SCHEDULE_FORM_RANGES = [
  'UNDER_100K',
  'FROM_100_TO_250K',
  'FROM_250_TO_500K',
  'FROM_500K_TO_1M',
  'OVER_1M',
] as const
type ScheduleFormRange = (typeof SCHEDULE_FORM_RANGES)[number]

const AUM_OPTIONS: { value: ScheduleFormRange; label: string }[] = [
  { value: 'UNDER_100K', label: 'Less than $100K' },
  { value: 'FROM_100_TO_250K', label: '$100K-$249K' },
  { value: 'FROM_250_TO_500K', label: '$250K-$499K' },
  { value: 'FROM_500K_TO_1M', label: '$500K-$999K' },
  { value: 'OVER_1M', label: '$1MM+' },
]

const isQualified = (aum: ScheduleFormRange) => aum !== 'UNDER_100K'

const schema = z.object({
  first_name: z
    .string()
    .min(1, 'Please enter your first name.')
    .max(LEAD_NAME_MAX_LENGTH.firstName, `First name must be ${LEAD_NAME_MAX_LENGTH.firstName} characters or fewer.`),
  last_name: z
    .string()
    .min(1, 'Please enter your last name.')
    .max(LEAD_NAME_MAX_LENGTH.lastName, `Last name must be ${LEAD_NAME_MAX_LENGTH.lastName} characters or fewer.`),
  investable_assets_range: z.preprocess(
    (val) => (val === undefined || val === null ? '' : val),
    z.string().min(1, 'Please select your estimated investable assets.').pipe(z.enum(SCHEDULE_FORM_RANGES))
  ),
  email: z.string().email('Please enter a valid email address.'),
  phone: z.preprocess(
    (val) => (val === undefined || val === null ? '' : val),
    z
      .string()
      .min(10, 'Please enter your phone number.')
      .max(10, 'Please enter your phone number.')
      .refine((phone) => /^[2-9]/.test(phone), 'Please enter a valid phone number.')
      .transform((phone) => `+1${phone}`)
  ),
})

type FormInput = z.input<typeof schema>
type FormValues = z.output<typeof schema>

type View = 'form' | 'otp' | 'disqualified'

export function ScheduleFormCard() {
  const router = useRouter()
  const { trackFlowComplete } = useVeritonic()
  const trackFormStepViewed = useTrackFormStepViewed()
  const [view, setView] = useState<View>('form')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [pendingData, setPendingData] = useState<FormValues | null>(null)

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
  })

  // Set to true when the user completes OTP and we hand off to Calendly.
  // The cleanup skips the clear in that case so CalendlyScheduler can still read it.
  const submittedRef = useRef(false)

  useEffect(() => {
    const prefill = loadScheduleFormPrefill()
    if (prefill) {
      setValue('first_name', prefill.first_name)
      setValue('last_name', prefill.last_name)
      setValue('email', prefill.email)
      if (prefill.phone) {
        setValue('phone', prefill.phone.replace(/^\+1/, ''))
      }
    }
    return () => {
      // Clear on abandonment (user leaves without submitting).
      // Skip when the submit succeeded so CalendlyScheduler on /schedule can
      // still read the prefill that onOtpVerified saved right before router.push.
      if (!submittedRef.current) {
        clearSchedulePrefill()
      }
    }
  }, [setValue])

  const onSubmit = async (data: FormValues) => {
    setSubmitError(null)
    if (!isQualified(data.investable_assets_range)) {
      setView('disqualified')
      return
    }
    const sent = await sendOtpMessage(data.phone)
    if (!sent) {
      setSubmitError('Failed to send verification code. Please try again.')
      return
    }
    setPendingData(data)
    setView('otp')
    // Tracking is best-effort; never block the OTP dialog after SMS was sent.
    try {
      trackFormStepViewed({
        step: 'verify-phone',
        type: 'step',
        formName: 'schedule_form',
        ...getTrackingParams(),
      })
    } catch (e) {
      console.warn('trackFormStepViewed(verify-phone) failed', { error: e })
    }
  }

  const onOtpVerified = async () => {
    if (!pendingData) return

    const tracking = getTrackingParams()
    let success: boolean
    try {
      success = await createScheduleFormLead(
        {
          first_name: pendingData.first_name,
          last_name: pendingData.last_name,
          email: pendingData.email,
          phone: pendingData.phone,
          investable_assets_range: pendingData.investable_assets_range,
        },
        tracking
      )
    } catch {
      setSubmitError('Something went wrong. Please try again.')
      setView('form')
      return
    }

    if (!success) {
      setSubmitError('Something went wrong. Please try again.')
      setView('form')
      return
    }

    // Lead created — tracking is best-effort; never blocks routing.
    try {
      trackFlowComplete()
      trackFormStepViewed({
        step: 'Submit',
        type: 'form',
        formName: 'schedule_form',
        formAction: 'submitted',
        ...tracking,
        investable_assets_range: pendingData.investable_assets_range,
      })
    } catch (e) {
      console.warn('trackFlowComplete/trackFormStepViewed failed', { error: e })
    }

    const pii = {
      email: pendingData.email,
      first_name: pendingData.first_name,
      last_name: pendingData.last_name,
      phone: pendingData.phone,
    }
    submittedRef.current = true
    saveSchedulePrefill(pii)
    router.push(buildScheduleRedirectUrl(PAID_MEDIA_CALENDLY_URL, tracking))
  }

  if (view === 'disqualified') {
    return <DisqualifiedView />
  }

  return (
    <>
      {view === 'otp' && pendingData && (
        <OtpDialog open={true} phoneNumber={pendingData.phone} onVerified={onOtpVerified} />
      )}

      <div inert={view === 'otp'} className="w-full max-w-lg rounded-2xl bg-white p-8">
        <p className="text-title-xs mb-3 font-bold text-gray-dark">Schedule a free call</p>
        <p className="text-body-sm text-neutral-darker">
          We are a fee-only firm with transparent fees that max out at 0.9% if you invest $100K+. Our advisors
          specialize in helping high-earning professionals, keeping their plan and investments aligned with evolving
          career changes and priorities. We regularly hold strategy sessions and provide clear advice when big financial
          decisions arise.
        </p>

        <hr className="my-6 border-smoke" />

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Name row */}
          <div className={cn('mb-4 flex gap-3', 'flex-col tablet:flex-row')}>
            <div className="flex flex-1 flex-col gap-2">
              <label htmlFor="first_name" className="text-subtitle font-bold text-neutral-darker uppercase">
                First Name
              </label>
              <Input
                id="first_name"
                type="text"
                autoComplete="given-name"
                error={errors.first_name?.message ?? null}
                {...register('first_name')}
              />
            </div>
            <div className="flex flex-1 flex-col gap-2">
              <label htmlFor="last_name" className="text-subtitle font-bold text-neutral-darker uppercase">
                Last Name
              </label>
              <Input
                id="last_name"
                type="text"
                autoComplete="family-name"
                error={errors.last_name?.message ?? null}
                {...register('last_name')}
              />
            </div>
          </div>

          {/* Estimated Investable Assets */}
          <Controller
            name="investable_assets_range"
            control={control}
            defaultValue={undefined}
            render={({ field }) => (
              <RangeSelectInput
                id="investable_assets_range"
                label="Estimated Investable Assets"
                name={field.name}
                ref={field.ref}
                value={field.value as string | undefined}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={AUM_OPTIONS}
                hint="Do not include 401(k), 403(b), or other employer-connected retirement accounts."
                error={errors.investable_assets_range?.message}
              />
            )}
          />

          {/* Email */}
          <div className="mb-4 flex flex-col gap-2">
            <label htmlFor="email" className="text-subtitle font-bold text-neutral-darker uppercase">
              Email Address
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              error={errors.email?.message ?? null}
              {...register('email')}
            />
          </div>

          {/* Phone */}
          <div className="mb-4 flex flex-col gap-2">
            <label htmlFor="phone" className="text-subtitle font-bold text-neutral-darker uppercase">
              Phone Number
            </label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <NumberInput
                  id="phone"
                  name={field.name}
                  ref={field.ref}
                  value={(field.value as string | undefined) ?? ''}
                  onBlur={field.onBlur}
                  onChange={(e) => field.onChange(e.target.value)}
                  mask="phone"
                  placeholder="(555) 555-5555"
                  autoComplete="tel"
                  error={errors.phone?.message}
                />
              )}
            />
          </div>

          {/* Consent intro */}
          <p className="mb-3 text-body-sm text-charcoal">
            By clicking &ldquo;Continue&rdquo; below, I agree to the following:
          </p>

          {/* Disclosures */}
          <div className="mb-6 text-body-xs text-neutral-darker">
            <p className="mb-4">
              <strong className="mb-1 block text-body-xs font-bold text-gray-dark">Terms and Disclosures</strong>I
              acknowledge and agree to NerdWallet Wealth Partner&apos;s{' '}
              <a
                href="https://www.nerdwallet.com/p/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green underline"
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                href="https://www.nerdwallet.com/p/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green underline"
              >
                Privacy Policy
              </a>
              , and confirm receipt of NerdWallet Wealth Partner&apos;s{' '}
              <a
                href="https://adviserinfo.sec.gov/firm/brochure/337328"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green underline"
              >
                Form ADV Part 2A
              </a>{' '}
              and{' '}
              <a
                href="https://reports.adviserinfo.sec.gov/crs/crs_337328.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green underline"
              >
                Form CRS
              </a>
              .
            </p>
            <p>
              <strong className="mb-1 block text-body-xs font-bold text-gray-dark">
                Consent to Marketing Calls and Texts
              </strong>
              I provide my express written consent to receive marketing calls and text messages at the phone number I
              provided above from NerdWallet Wealth Partners, including their agents and service providers. These
              communications may be sent by using automated technology, including an autodialer, selection system, or
              prerecorded or artificial voice. I provide this consent even if my number is listed on any
              company-specific, state, or federal Do-Not-Call list, or if I have previously opted out of receiving
              telemarketing calls or text messages from NerdWallet. I understand that I may opt out of receiving calls
              or text messages at any time. I certify that the phone number I provided is my own and I am permitted to
              receive calls and texts at that number, and I agree to promptly notify NerdWallet Wealth Partners if I
              stop using the number entered above. Message and data rates may apply. Consent is not required as a
              condition of using these services.
            </p>
          </div>

          {submitError && <p className="mb-3 text-body-sm text-red">{submitError}</p>}
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className="sticky bottom-4 z-10 w-full rounded-lg bg-green p-4 text-body font-bold text-white transition-colors hover:bg-green-dark active:bg-green-darker disabled:cursor-not-allowed disabled:bg-neutral-light tablet:static"
          >
            {isSubmitting ? 'Sending code…' : 'Continue'}
          </button>
        </form>
      </div>
    </>
  )
}
